import { db, collection, addDoc, serverTimestamp, auth } from '../firebase';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AUDIT_OMITTED_FIELDS = new Set(['actionLog', 'createdAt', 'updatedAt']);

function sanitizeMetadataValue(value: any, key?: string): any {
  if (value === undefined || AUDIT_OMITTED_FIELDS.has(key || '')) return undefined;
  if (value === null || typeof value !== 'object') return value;

  // Firestore transform sentinels cannot be nested inside an audit document.
  // The outer activity log has its own server timestamp, so omit these values
  // from snapshots rather than allowing a successful business write to fail log creation.
  const methodName = String(value?._methodName || '');
  if (methodName === 'serverTimestamp' || methodName === 'arrayUnion' || methodName === 'arrayRemove' || methodName === 'increment') {
    return undefined;
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(item => sanitizeMetadataValue(item)).filter(item => item !== undefined);

  return Object.fromEntries(
    Object.entries(value)
      .map(([entryKey, entryValue]) => [entryKey, sanitizeMetadataValue(entryValue, entryKey)] as const)
      .filter(([, entryValue]) => entryValue !== undefined)
  );
}

export interface LogEntry {
  orgId: string;
  userId: string;
  userName: string;
  userEmail: string;
  module: string;
  action: string;
  targetId?: string;
  targetName?: string;
  metadata?: {
    before?: any;
    after?: any;
    reason?: string;
    pageName?: string;
    ipAddress?: string;
    userAgent?: string;
  };
}

const normalizeAction = (action: string, metadata?: any) => {
  const normalized = String(action || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  const metadataType = String(metadata?.type || '').toUpperCase();
  const fromStatus = String(metadata?.from ?? metadata?.before?.status ?? '').trim().toLowerCase();
  const toStatus = String(metadata?.to ?? metadata?.after?.status ?? '').trim().toLowerCase();
  const approvalStatuses = new Set(['approved', 'accepted', 'awaiting payment', 'paid']);
  const removalStatuses = new Set(['void', 'deleted', 'cancelled', 'canceled']);

  if (metadataType === 'PAYMENT_REVERSED' || normalized.includes('PAYMENT_REVERSED') || normalized.includes('PAYMENT_REMOV') || normalized.includes('REVERSE_PAYMENT') || normalized.includes('REVERSED_PAYMENT')) return 'PAYMENT_REVERSED';
  if (metadataType === 'PAYMENT_RECEIVED' || normalized.includes('PAYMENT_RECEIVED') || normalized.includes('PAYMENT') || normalized === 'ADDED_PAYMENT') return 'PAYMENT';
  if (['CREATE', 'APPROVE', 'EDIT', 'PAYMENT', 'PAYMENT_REVERSED', 'REMOVE', 'ADD'].includes(normalized)) return normalized;
  if (['SCREENSHOT', 'TAB_SWITCH'].includes(normalized)) return normalized;
  if (approvalStatuses.has(toStatus) && !approvalStatuses.has(fromStatus)) return 'APPROVE';
  if (removalStatuses.has(toStatus) && !removalStatuses.has(fromStatus)) return 'REMOVE';
  if (normalized === 'APPROVED' || normalized === 'ACCEPTED' || normalized.includes('APPROVED') || normalized.includes('APPROVAL')) return 'APPROVE';
  if (normalized.includes('CREAT') || normalized.startsWith('COPIED_') || normalized.startsWith('IMPORTED') || normalized.includes('GENERAT')) return 'CREATE';
  if (normalized.includes('DELET') || normalized.includes('REMOV') || normalized.includes('VOID') || normalized.includes('CANCEL') || normalized.includes('RETURN')) return 'REMOVE';
  if (normalized.includes('ATTACH') || normalized.includes('UPLOAD') || normalized.includes('SHARE') || normalized.includes('RECEIV')) return 'ADD';
  if (normalized === 'EDITED' || normalized === 'UPDATED' || normalized.includes('STATUS_CHANGE') || normalized === 'UPDATED_STATUS' || normalized === 'STATUS_UPDATED') return 'EDIT';
  return 'EDIT';
};

export const Logger = {
  /** Write one canonical activity-log event. User-authored notes are stored separately. */
  log: async (orgId: string, action: string, module: string, targetId?: string, targetName?: string, metadata?: any) => {
    try {
      const user = auth.currentUser;
      const userName = user?.displayName || user?.email || 'System';
      const userEmail = user?.email || 'unknown';
      const canonicalAction = normalizeAction(action, metadata);
      const canonicalMetadata = sanitizeMetadataValue({
        ...(metadata && typeof metadata === 'object' ? metadata : {}),
        source: metadata?.source || 'application',
      }) || {};
      const appAuthToken = typeof localStorage !== 'undefined'
        ? localStorage.getItem('app_auth_token')
        : null;

      if (appAuthToken) {
        const activityLog = {
          module,
          action: canonicalAction,
          ...(targetId && UUID_REGEX.test(targetId) ? { targetId } : {}),
          targetName: targetName || null,
          metadata: canonicalMetadata,
          userName,
          userEmail
        };

        try {
          const response = await fetch(`/api/organizations/${orgId}/activity-logs`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${appAuthToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(activityLog)
          });

          if (!response.ok) {
            console.error(`Local activity log request failed: ${response.status}`);
          }
        } catch (error) {
          console.error('Local activity log request failed:', error);
        }
        return;
      }

      await addDoc(collection(db, 'activity_logs'), {
        orgId,
        userId: user?.uid || 'unknown',
        userName,
        userEmail,
        module,
        action: canonicalAction,
        targetId: targetId || null,
        targetName: targetName || null,
        metadata: canonicalMetadata,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  },

  /**
   * Specific helper for logging edits with before/after state
   */
  logEdit: async (orgId: string, module: string, targetId: string, targetName: string, before: any, after: any) => {
    return Logger.log(orgId, 'EDIT', module, targetId, targetName, { before, after });
  },

  /**
   * Helper for security alerts
   */
  logSecurity: async (orgId: string, action: 'SCREENSHOT' | 'TAB_SWITCH', pageName: string) => {
    return Logger.log(orgId, action, 'Security', undefined, pageName, { 
      pageName,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
    });
  }
};

// Keep legacy export for existing components to avoid breakage
export async function logHistory(orgId: string, action: string, details: string, _user: any, entityId: string, entityType: string) {
  return Logger.log(orgId, action, entityType, entityId, undefined, { details, reason: details });
}
